/**
 * Vaccination Service
 * CRUD operations for Vaccinations and VaccinationTemplates collections
 *
 * @collection Users/{userId}/Vaccinations
 * @collection Users/{userId}/VaccinationTemplates
 */

import { db } from "../../../firebase";

// ============================================================================
// VACCINATIONS — WRITE
// ============================================================================

/**
 * Add a vaccination record
 * @param {string} userId
 * @param {string} mac - Animal MAC
 * @param {Object} data - { vaccineName, batchNumber, dosage, route, administeredDate, nextDueDate, templateId, notes }
 * @returns {Promise<string>} Document ID
 */
export async function addVaccination(userId, mac, data) {
	const timestamp = Date.now();
	const docId = timestamp.toString();

	const cleanData = Object.fromEntries(
		Object.entries(data).filter(([, v]) => v !== undefined)
	);

	const record = {
		...cleanData,
		MAC: mac,
		Uid: userId,
		t: timestamp,
		Archived: false,
	};

	await db
		.collection("Users")
		.doc(userId)
		.collection("Vaccinations")
		.doc(docId)
		.set(record);

	return docId;
}

/**
 * Add batch vaccinations (same vaccine for multiple animals)
 * @param {string} userId
 * @param {Array<string>} macs - Array of animal MACs
 * @param {Object} data - Shared vaccination data
 * @returns {Promise<Array<string>>} Array of document IDs
 */
export async function addBatchVaccinations(userId, macs, data) {
	const batch = db.batch();
	const docIds = [];

	for (const mac of macs) {
		const timestamp = Date.now();
		const docId = `${timestamp}_${mac}`;
		docIds.push(docId);

		const cleanData = Object.fromEntries(
			Object.entries(data).filter(([, v]) => v !== undefined)
		);

		const record = {
			...cleanData,
			MAC: mac,
			Uid: userId,
			t: timestamp,
			Archived: false,
		};

		const ref = db
			.collection("Users")
			.doc(userId)
			.collection("Vaccinations")
			.doc(docId);

		batch.set(ref, record);
	}

	await batch.commit();
	return docIds;
}

/**
 * Update a vaccination record
 */
export async function updateVaccination(userId, docId, updates) {
	const cleanUpdates = Object.fromEntries(
		Object.entries(updates).filter(([, v]) => v !== undefined)
	);

	await db
		.collection("Users")
		.doc(userId)
		.collection("Vaccinations")
		.doc(docId)
		.update({ ...cleanUpdates, updatedAt: Date.now() });
}

/**
 * Archive a vaccination record
 */
export async function archiveVaccination(userId, docId) {
	await updateVaccination(userId, docId, { Archived: true, ArchivedAt: Date.now() });
}

// ============================================================================
// VACCINATIONS — READ
// ============================================================================

/**
 * Get all vaccinations for an animal
 */
export async function getVaccinationsByAnimal(userId, mac) {
	const snapshot = await db
		.collection("Users")
		.doc(userId)
		.collection("Vaccinations")
		.where("MAC", "==", mac)
		.where("Archived", "==", false)
		.orderBy("t", "desc")
		.get();

	return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
}

/**
 * Get all vaccinations for a user
 */
export async function getAllVaccinations(userId) {
	const snapshot = await db
		.collection("Users")
		.doc(userId)
		.collection("Vaccinations")
		.where("Archived", "==", false)
		.orderBy("t", "desc")
		.get();

	return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
}

/**
 * Get overdue vaccinations (nextDueDate < now)
 */
export async function getOverdueVaccinations(userId) {
	const now = Date.now();
	const all = await getAllVaccinations(userId);
	return all.filter((v) => {
		if (!v.nextDueDate) return false;
		const dueTs = typeof v.nextDueDate === "number" ? v.nextDueDate : new Date(v.nextDueDate).getTime();
		return dueTs < now;
	});
}

/**
 * Get upcoming vaccinations (nextDueDate within N days)
 * @param {string} userId
 * @param {number} days - Look-ahead window (default: 7)
 */
export async function getUpcomingVaccinations(userId, days = 7) {
	const now = Date.now();
	const windowEnd = now + days * 24 * 60 * 60 * 1000;
	const all = await getAllVaccinations(userId);
	return all.filter((v) => {
		if (!v.nextDueDate) return false;
		const dueTs = typeof v.nextDueDate === "number" ? v.nextDueDate : new Date(v.nextDueDate).getTime();
		return dueTs >= now && dueTs <= windowEnd;
	});
}

// ============================================================================
// VACCINATION TEMPLATES — WRITE
// ============================================================================

/**
 * Create a vaccination template (farm-wide vaccination protocol)
 * @param {string} userId
 * @param {Object} data - { name, vaccinations[], targetLifecycleStage, active }
 * @returns {Promise<string>} Document ID
 */
export async function createVaccinationTemplate(userId, data) {
	const timestamp = Date.now();
	const docId = timestamp.toString();

	const record = {
		...data,
		Uid: userId,
		t: timestamp,
		active: data.active !== false,
	};

	await db
		.collection("Users")
		.doc(userId)
		.collection("VaccinationTemplates")
		.doc(docId)
		.set(record);

	return docId;
}

/**
 * Update a vaccination template
 */
export async function updateVaccinationTemplate(userId, docId, updates) {
	await db
		.collection("Users")
		.doc(userId)
		.collection("VaccinationTemplates")
		.doc(docId)
		.update({ ...updates, updatedAt: Date.now() });
}

/**
 * Delete (archive) a vaccination template
 */
export async function deleteVaccinationTemplate(userId, docId) {
	await updateVaccinationTemplate(userId, docId, { active: false, deletedAt: Date.now() });
}

// ============================================================================
// VACCINATION TEMPLATES — READ
// ============================================================================

/**
 * Get all active vaccination templates
 */
export async function getVaccinationTemplates(userId) {
	const snapshot = await db
		.collection("Users")
		.doc(userId)
		.collection("VaccinationTemplates")
		.where("active", "==", true)
		.get();

	return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
}

/**
 * Get a single vaccination template
 */
export async function getVaccinationTemplate(userId, docId) {
	const doc = await db
		.collection("Users")
		.doc(userId)
		.collection("VaccinationTemplates")
		.doc(docId)
		.get();

	return doc.exists ? { id: doc.id, ...doc.data() } : null;
}

// ============================================================================
// COMPLIANCE
// ============================================================================

/**
 * Calculate vaccination compliance for an animal against a template
 * @param {Array} vaccinations - Animal's vaccination records
 * @param {Object} template - Vaccination template
 * @returns {{ completed: number, total: number, percent: number, missing: Array }}
 */
export function calculateVaccinationCompliance(vaccinations, template) {
	if (!template?.vaccinations?.length) {
		return { completed: 0, total: 0, percent: 100, missing: [] };
	}

	const total = template.vaccinations.length;
	const givenNames = new Set(vaccinations.map((v) => v.vaccineName?.toLowerCase()));
	const missing = template.vaccinations.filter(
		(v) => !givenNames.has(v.name?.toLowerCase())
	);
	const completed = total - missing.length;
	const percent = total > 0 ? Math.round((completed / total) * 100) : 100;

	return { completed, total, percent, missing };
}
